import {
  Button,
  paletteHelper,
  radiusHelper,
  spacingHelper,
  typographyHelper,
} from '@property.com.au/ui-kit';
import type { FunctionComponent } from 'react';
import styled, { css } from 'styled-components';
import { mapCommuteMethodToIcon } from '../../helpers/mapCommuteMethodToIcon';
import { mapCommuteMethodToLabelText } from '../../helpers/mapCommuteMethodToLabelText';
import type { CommuteMethod } from '../../types';

const Label = styled.label`
  position: relative;
`;

const HiddenRadioInput = styled.input`
  opacity: 0;
  position: absolute;
  height: 100%;
  width: 100%;

  &:focus + .commuteMethodButton::after {
    content: '';
    display: block;
    position: absolute;
    inset: -0.3125rem;
    border: 0.125rem solid ${paletteHelper('borderFocused')};
    border-radius: 0.75rem;
  }
`;

const selectedCommuteButtonStyles = css`
  background-color: ${paletteHelper('backgroundSelectedBrand')};
  color: ${paletteHelper('textSelectedBrand')};
  border-color: ${paletteHelper('borderHoveredBrand')};

  &:focus,
  &:focus-within,
  &:hover {
    background-color: ${paletteHelper('backgroundSelectedBrand')};
    color: ${paletteHelper('textSelectedBrand')};
    border-color: ${paletteHelper('borderHoveredBrand')};
  }
`;

const CommuteMethodButton = styled(Button)<{ $isSelected: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${spacingHelper('extraSmall')};
  ${typographyHelper('subtitle02')};
  color: ${paletteHelper('textPrimary')};
  background: ${paletteHelper('backgroundPrimary')};
  border: 1px solid ${paletteHelper('borderPrimary')};
  border-radius: ${radiusHelper('medium')};
  padding: 1rem;
  height: 100%;

  &:hover {
    border-color: ${paletteHelper('borderHovered')};
    background-color: ${paletteHelper('backgroundPrimary')};
    color: ${paletteHelper('textPrimary')};
  }

  &:focus,
  &:focus-within {
    border-color: ${paletteHelper('borderPrimary')};
    background-color: ${paletteHelper('backgroundPrimary')};
    color: ${paletteHelper('textPrimary')};
  }

  ${({ $isSelected }) => $isSelected && selectedCommuteButtonStyles}
  &:active {
    ${selectedCommuteButtonStyles}
  }
`;

interface CommuteMethodRadioInputProps {
  method: CommuteMethod;
  isSelected: boolean;
  onChange: (method: CommuteMethod) => void;
}

export const CommuteMethodRadioInput: FunctionComponent<CommuteMethodRadioInputProps> = ({
  method,
  isSelected,
  onChange,
}) => {
  const inputName = `commute_method_${method}`;

  return (
    <Label htmlFor={inputName}>
      <HiddenRadioInput
        checked={isSelected}
        id={inputName}
        name={inputName}
        type="radio"
        value={method}
        onChange={() => onChange(method)}
      />
      <CommuteMethodButton
        $isSelected={isSelected}
        aria-selected={isSelected}
        forwardedAs="div"
        className="commuteMethodButton"
      >
        {mapCommuteMethodToIcon(method, 'Lg')}
        {mapCommuteMethodToLabelText(method)}
      </CommuteMethodButton>
    </Label>
  );
};
